"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";

// --- Module-level regexes & helpers (mirrors the approach in citation-check-mla) ---

const RE_BLANK_LINE = /\n[ \t]*\n/;
const RE_NEWLINE = /\n/;
/*
 * Normalisation by Unicode property, not by `a-z0-9` — the most damaging of the fleet's
 * ASCII regexes, because it did not fail quietly. It gave a confident wrong answer.
 *
 * `normalizeEntry` lower-cases an entry and then deletes everything this class does not
 * name. Every non-ASCII letter was deleted, so a Russian, Arabic, Hindi, Korean or
 * Chinese reference collapsed to nothing but its numbers: "Кузнецов, А. (2023). Сон и
 * память. Журнал сна, 12(3), 45-67." normalised to "2023 12 3 45 67", and so did an
 * entirely different paper by an entirely different author in the same issue. Two
 * unrelated references then compared as an EXACT duplicate — similarity 1.00 — and the
 * tool told the reader to delete one of them. Measured on real pairs: ru, hi, ar and ko
 * all reported `exact` on two different papers; with the fix they score 0.43, 0.42, 0.45
 * and 0.50, all under the 0.8 threshold. Vietnamese and German were the quieter half of
 * the same bug, keeping only the un-accented fragments ("m ller j ... ged chtnis"), which
 * both loses real duplicates and manufactures overlap between unrelated words.
 *
 * `\p{M}` is in the keep-set for the reason the headline-analyzer records: Devanagari
 * writes its vowels as combining marks, so without it every matra becomes a space and
 * one Hindi word turns into three tokens, which is exactly the word-overlap score this
 * tool is built on.
 */
const RE_NON_ALNUM = /[^\p{L}\p{N}\p{M}\s]+/gu;
const RE_SPACES = /\s+/g;
const RE_WORD_SPLIT = /\s+/;

const NEAR_DUPLICATE_THRESHOLD = 0.8;

/**
 * Split pasted text into individual reference entries. Blank-line-separated
 * blocks are one entry each (hanging-indent wrap lines rejoined); otherwise
 * each non-empty line is its own entry.
 */
function splitEntries(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) {
    return [];
  }
  if (RE_BLANK_LINE.test(trimmed)) {
    return trimmed
      .split(RE_BLANK_LINE)
      .map((block) =>
        block
          .split(RE_NEWLINE)
          .map((line) => line.trim())
          .join(" ")
          .trim()
      )
      .filter(Boolean);
  }
  return trimmed
    .split(RE_NEWLINE)
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Lowercase, strip punctuation, collapse whitespace. */
function normalizeEntry(entry: string): string {
  return entry
    .toLowerCase()
    .replace(RE_NON_ALNUM, " ")
    .replace(RE_SPACES, " ")
    .trim();
}

/** Dice coefficient on the word sets of two normalized entries (0..1). */
function diceSimilarity(a: string, b: string): number {
  const wordsA = new Set(a.split(RE_WORD_SPLIT).filter(Boolean));
  const wordsB = new Set(b.split(RE_WORD_SPLIT).filter(Boolean));
  if (wordsA.size === 0 || wordsB.size === 0) {
    return 0;
  }
  let shared = 0;
  for (const word of wordsA) {
    if (wordsB.has(word)) {
      shared += 1;
    }
  }
  return (2 * shared) / (wordsA.size + wordsB.size);
}

interface DuplicatePair {
  indexA: number;
  indexB: number;
  similarity: number;
  exact: boolean;
}

interface DuplicateGroup {
  /** Indexes into the entries array, ascending. */
  members: number[];
  /** Lowest pairwise similarity inside the group. */
  minSimilarity: number;
  exact: boolean;
}

interface Report {
  entries: string[];
  groups: DuplicateGroup[];
}

/** Find all exact/near-duplicate pairs among normalized entries. */
function findPairs(normalized: string[]): DuplicatePair[] {
  const pairs: DuplicatePair[] = [];
  for (let i = 0; i < normalized.length; i++) {
    for (let j = i + 1; j < normalized.length; j++) {
      const exact = normalized[i] === normalized[j];
      const similarity = exact
        ? 1
        : diceSimilarity(normalized[i], normalized[j]);
      if (exact || similarity >= NEAR_DUPLICATE_THRESHOLD) {
        pairs.push({ indexA: i, indexB: j, similarity, exact });
      }
    }
  }
  return pairs;
}

/** Union-find root lookup with path compression. */
function findRoot(parent: number[], index: number): number {
  let root = index;
  while (parent[root] !== root) {
    parent[root] = parent[parent[root]];
    root = parent[root];
  }
  return root;
}

/** Merge overlapping duplicate pairs into groups of connected entries. */
function groupPairs(pairs: DuplicatePair[], count: number): DuplicateGroup[] {
  const parent = Array.from({ length: count }, (_, i) => i);
  for (const pair of pairs) {
    const rootA = findRoot(parent, pair.indexA);
    const rootB = findRoot(parent, pair.indexB);
    if (rootA !== rootB) {
      parent[rootB] = rootA;
    }
  }
  const byRoot = new Map<number, DuplicateGroup>();
  for (const pair of pairs) {
    const root = findRoot(parent, pair.indexA);
    const group = byRoot.get(root) ?? {
      members: [],
      minSimilarity: 1,
      exact: true,
    };
    for (const index of [pair.indexA, pair.indexB]) {
      if (!group.members.includes(index)) {
        group.members.push(index);
      }
    }
    group.minSimilarity = Math.min(group.minSimilarity, pair.similarity);
    group.exact = group.exact && pair.exact;
    byRoot.set(root, group);
  }
  const groups = [...byRoot.values()];
  for (const group of groups) {
    group.members.sort((a, b) => a - b);
  }
  groups.sort((a, b) => a.members[0] - b.members[0]);
  return groups;
}

function buildReport(text: string): Report {
  const entries = splitEntries(text);
  const normalized = entries.map(normalizeEntry);
  const pairs = findPairs(normalized);
  return { entries, groups: groupPairs(pairs, entries.length) };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function groupLabel(
  group: DuplicateGroup,
  strings: typeof DUPLICATE_CITATION_FINDER_STRINGS
): string {
  if (group.exact) {
    return strings.exactDuplicate;
  }
  return t(strings.likelyDuplicate, {
    percent: Math.round(group.minSimilarity * 100),
  });
}

/** English strings. A translated content file overrides these through `ui`. */
export const DUPLICATE_CITATION_FINDER_STRINGS = {
  referenceListLabel:
    "Reference list — one entry per line, or separated by blank lines",
  placeholder:
    "Smith, J. M. (2023). Sleep and memory. Journal of Sleep, 12(3), 45-67.\nDoe, J. (2022). Attention in the classroom. Education Today, 8(1), 1-15.\nSmith, J. M. (2023). Sleep and memory. Journal of Sleep, 12(3), 45-67. https://doi.org/10.1000/xyz",
  /*
   * One message, because the old shape could not be translated at all.
   *
   * This read `{count}{" entr"}{count === 1 ? "y" : "ies"} detected` — a STEM in the
   * strings, an English suffix rule in the JSX, and "detected" hardcoded outside the
   * strings entirely. A translator handed " entr" can do nothing useful with it: any
   * word they write gets "y" or "ies" stapled on. Two locales supplied a value anyway
   * and the rest correctly left it blank, which is why this was a named exemption in
   * `tool-plurals.test.ts` rather than a bug anyone could fix from a content file.
   */
  entriesDetected:
    "one: {count} entry detected|other: {count} entries detected",
  findDuplicates: "Find Duplicates",
  clear: "Clear",
  entriesChecked: "Entries checked",
  duplicateGroups: "Duplicate groups",
  entriesFlagged: "Entries flagged",
  noDuplicatesFound: "No duplicates found",
  noDuplicatesBody:
    "one:No exact or near-duplicate pairs (at or above 80% word overlap) were detected among the {count} entry.|other:No exact or near-duplicate pairs (at or above 80% word overlap) were detected among the {count} entries.",
  flaggedGroups: "Flagged groups",
  similarityHeuristicNote:
    "Similarity is a heuristic word-overlap score, not a bibliographic match — review each group yourself before deleting an entry. Two different papers by the same authors can look alike.",
  exactDuplicate: "Exact duplicate",
  likelyDuplicate: "Likely duplicate — {percent}% similar",
  entryHashPrefix: "#",
} as const;

export default function DuplicateCitationFinderWidget() {
  const [input, setInput] = useState("");
  const [report, setReport] = useState<Report | null>(null);

  const strings = useToolUi(DUPLICATE_CITATION_FINDER_STRINGS);
  const locale = useToolLocale();

  const entryCount = splitEntries(input).length;

  function handleCheck() {
    if (input.trim().length === 0) {
      return;
    }
    setReport(buildReport(input));
  }

  function handleClear() {
    setInput("");
    setReport(null);
  }

  const flaggedCount =
    report?.groups.reduce((sum, g) => sum + g.members.length, 0) ?? 0;

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="duplicate-input"
        >
          {strings.referenceListLabel}
        </label>
        <textarea
          className="min-h-[240px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="duplicate-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {tPlural(strings.entriesDetected, locale, entryCount)}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleCheck}
          type="button"
        >
          {strings.findDuplicates}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && report === null}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Stats */}
      {report && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.entriesChecked}
            value={report.entries.length}
          />
          <StatCard
            label={strings.duplicateGroups}
            value={report.groups.length}
          />
          <StatCard label={strings.entriesFlagged} value={flaggedCount} />
        </div>
      )}

      {/* Results */}
      {report && report.groups.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-6 text-center">
          <p className="font-medium text-foreground">
            {strings.noDuplicatesFound}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {tPlural(strings.noDuplicatesBody, locale, report.entries.length)}
          </p>
        </div>
      )}
      {report && report.groups.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.flaggedGroups}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.similarityHeuristicNote}
          </p>
          {report.groups.map((group) => (
            <div
              className="space-y-3 rounded-lg border border-border bg-card p-5"
              key={group.members.join("-")}
            >
              <p className="font-medium text-foreground text-sm">
                {groupLabel(group, strings)}
              </p>
              <ul className="space-y-2">
                {group.members.map((index) => (
                  <li
                    className="rounded-lg bg-muted/50 p-3 font-mono text-foreground text-sm leading-relaxed"
                    key={index}
                  >
                    <span className="mr-2 text-muted-foreground">
                      {strings.entryHashPrefix}
                      {index + 1}
                    </span>
                    {report.entries[index]}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}
    </>
  );
}
